import { getGymSchedule } from '../services/dataService';

const SCHEDULE_KEY = 'sm_fitness_gym_schedule';
const COMPLETIONS_KEY = 'sm_fitness_completions';
const STEP_KEY = 'sm_fitness_steps';
const STEP_GOAL_KEY = 'sm_fitness_step_goal';
const SPORT_SESSIONS_KEY = 'sm_fitness_sport_sessions';
const CUSTOM_SPORTS_KEY = 'sm_fitness_custom_sports';

// Returns the default schedule from /data/gym-schedule.json.
// Evaluated lazily (not at module load) so dataService.init() has run first.
export function getDefaultSchedule() {
  return getGymSchedule() ?? {};
}

// ── Gym Schedule ──────────────────────────────────────────────────────────────

export function getSchedule() {
  try {
    const stored = localStorage.getItem(SCHEDULE_KEY);
    return stored ? JSON.parse(stored) : { ...getDefaultSchedule() };
  } catch {
    return { ...getDefaultSchedule() };
  }
}

export function saveSchedule(schedule) {
  localStorage.setItem(SCHEDULE_KEY, JSON.stringify(schedule));
}

export function getCompletions() {
  try {
    const stored = localStorage.getItem(COMPLETIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function toggleExercise(dayKey, exercise) {
  const today = new Date().toISOString().split('T')[0];
  const key = `${today}_${dayKey}`;
  const completions = getCompletions();
  const dayDone = completions[key] || [];
  completions[key] = dayDone.includes(exercise)
    ? dayDone.filter(e => e !== exercise)
    : [...dayDone, exercise];
  localStorage.setItem(COMPLETIONS_KEY, JSON.stringify(completions));
  return completions[key];
}

export function getTodayCompletions(dayKey) {
  const today = new Date().toISOString().split('T')[0];
  return (getCompletions()[`${today}_${dayKey}`]) || [];
}

// ── Step Tracking ─────────────────────────────────────────────────────────────

export function getSteps() {
  try {
    const stored = localStorage.getItem(STEP_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function getStepGoal() {
  return parseInt(localStorage.getItem(STEP_GOAL_KEY) || '10000', 10);
}

export function saveStepGoal(goal) {
  localStorage.setItem(STEP_GOAL_KEY, String(goal));
}

export function getTodaySteps() {
  const today = new Date().toISOString().split('T')[0];
  const entry = getSteps().find(s => s.date === today);
  return entry ? entry.steps : 0;
}

export function logSteps(count) {
  const today = new Date().toISOString().split('T')[0];
  const steps = getSteps();
  const idx = steps.findIndex(s => s.date === today);
  if (idx >= 0) {
    steps[idx].steps = count;
  } else {
    steps.unshift({ date: today, steps: count });
  }
  localStorage.setItem(STEP_KEY, JSON.stringify(steps));
}

export function getWeekSteps() {
  const steps = getSteps();
  const now = new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    const date = d.toISOString().split('T')[0];
    const entry = steps.find(s => s.date === date);
    const day = d.toLocaleDateString('en-GB', { weekday: 'short' });
    return { date, day, steps: entry ? entry.steps : 0 };
  });
}

// ── Sport Sessions ────────────────────────────────────────────────────────────

export function getSportSessions() {
  try {
    const stored = localStorage.getItem(SPORT_SESSIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function saveSportSession(session) {
  const sessions = getSportSessions();
  const newSession = { ...session, id: Date.now().toString(), createdAt: new Date().toISOString() };
  sessions.unshift(newSession);
  localStorage.setItem(SPORT_SESSIONS_KEY, JSON.stringify(sessions));
  return newSession;
}

export function deleteSportSession(id) {
  const sessions = getSportSessions().filter(s => s.id !== id);
  localStorage.setItem(SPORT_SESSIONS_KEY, JSON.stringify(sessions));
}

export function toggleSportSession(id) {
  const sessions = getSportSessions();
  const idx = sessions.findIndex(s => s.id === id);
  if (idx >= 0) {
    sessions[idx].completed = !sessions[idx].completed;
    localStorage.setItem(SPORT_SESSIONS_KEY, JSON.stringify(sessions));
  }
}

export function getUpcomingSportSessions() {
  const today = new Date().toISOString().split('T')[0];
  return getSportSessions()
    .filter(s => s.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
}

// ── Custom Sports ────────────────────────────────────────────────────────────

export function getCustomSports() {
  try {
    const stored = localStorage.getItem(CUSTOM_SPORTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function saveCustomSport(sport) {
  const sports = getCustomSports();
  const entry = { ...sport, id: sport.id || `custom_${Date.now()}`, createdAt: new Date().toISOString() };
  sports.unshift(entry);
  localStorage.setItem(CUSTOM_SPORTS_KEY, JSON.stringify(sports));
  return entry;
}

export function deleteCustomSport(id) {
  const sports = getCustomSports().filter(s => s.id !== id);
  localStorage.setItem(CUSTOM_SPORTS_KEY, JSON.stringify(sports));
}

export function replaceCustomSports(sports) {
  localStorage.setItem(CUSTOM_SPORTS_KEY, JSON.stringify(sports));
}
